import { useMemo } from "react";
import { usePlansCatalog } from "@/features/billing/hooks/usePlansCatalog";
import {
  buildPlanUpgradeOffer,
  offerNeedsCatalog,
  type PlanUpgradeOffer,
} from "@/features/billing/utils/planUpgradeOffer";
import { hasPlanFeature, type PlanFeatureKey } from "./planFeature";
import { useSectorAccessState } from "./useSectorAccessState";
import { useUserSector } from "./useUserSector";

/**
 * Estado de um recurso diante do plano do setor ativo (SA-1401):
 * - `pending`: o access-state ainda não respondeu (ou não há setor ativo).
 *   Quem trava espera: nada de bloqueio piscando.
 * - `allowed`: o plano libera (trial, conta interna ou flag do plano).
 * - `error`: o access-state falhou sem dado nenhum (SA-1361, ruling F2-7). O
 *   plano é desconhecido: quem trava mostra o erro com "Tentar novamente"
 *   (`retry`), nunca "não está no plano"; o menu não trava item por plano.
 * - `locked`: fora do plano. A trava (e a ordem do menu) sai SÓ do
 *   access-state; `offer` é a oferta de upgrade e fica `null` enquanto o
 *   catálogo que ela precisa ainda carrega.
 */
export type PlanFeatureAccess =
  | { status: "pending" }
  | { status: "allowed" }
  | { status: "error"; retry: () => void; isRetrying: boolean }
  | { status: "locked"; offer: PlanUpgradeOffer | null };

export type PlanAccessByFeature = Partial<
  Record<PlanFeatureKey, PlanFeatureAccess>
>;

const PENDING: PlanFeatureAccess = { status: "pending" };
const ALLOWED: PlanFeatureAccess = { status: "allowed" };
const NO_FEATURES: readonly PlanFeatureKey[] = [];

/**
 * Estado de cada recurso pedido. `features` precisa ser estável (constante de
 * módulo ou memo): é dependência do memo da resposta.
 *
 * A regra de liberar é `hasPlanFeature`. Erro do access-state sem dado vira
 * `error` (antes caía no default sem subscription e travava tudo, dizendo "não
 * está no plano" sem saber o plano). O catálogo
 * (`/billing/plans`) só é pedido quando a oferta de algum recurso travado
 * depende dele (`offerNeedsCatalog`); se ele falha, o recurso trava sem nome
 * de plano mínimo.
 */
export function usePlanAccess(
  features: readonly PlanFeatureKey[]
): PlanAccessByFeature {
  const { activeSectorId } = useUserSector();
  const {
    data: access,
    isPending: isAccessQueryPending,
    isLoadingError,
    isFetching: isAccessFetching,
    refetch: refetchAccess,
  } = useSectorAccessState(activeSectorId);
  // Sem setor a query fica desligada e o TanStack diz `isLoading` false:
  // decidir aí seria decidir sobre o default.
  const isAccessPending = !activeSectorId || isAccessQueryPending;
  // Falhou sem dado nenhum: `data` é o default sem subscription, que não diz
  // nada sobre o plano. `isLoadingError` segue true durante o retry (o status
  // só sai de `error` com sucesso), então o card de erro não pisca.
  const isAccessError = !isAccessPending && isLoadingError === true;
  const currentPlan = {
    currentSlug: access.plan_slug,
    currentFeatures: access.plan_features,
  };
  const needsCatalog =
    !isAccessPending &&
    !isAccessError &&
    features.some(
      (feature) =>
        !hasPlanFeature(access, feature) &&
        offerNeedsCatalog({ feature, ...currentPlan })
    );
  const catalog = usePlansCatalog(activeSectorId, { enabled: needsCatalog });
  const plans = catalog.data?.plans;
  const isCatalogPending = catalog.isPending;

  const accessError = useMemo<PlanFeatureAccess | null>(
    () =>
      isAccessError
        ? {
            status: "error",
            retry: () => void refetchAccess(),
            isRetrying: isAccessFetching,
          }
        : null,
    [isAccessError, isAccessFetching, refetchAccess]
  );

  return useMemo(() => {
    const result: PlanAccessByFeature = {};
    for (const feature of features) {
      if (isAccessPending) {
        result[feature] = PENDING;
        continue;
      }
      if (accessError) {
        result[feature] = accessError;
        continue;
      }
      if (hasPlanFeature(access, feature)) {
        result[feature] = ALLOWED;
        continue;
      }
      const input = {
        feature,
        currentSlug: access.plan_slug,
        currentFeatures: access.plan_features,
      };
      const offerIsPending = offerNeedsCatalog(input) && isCatalogPending;
      result[feature] = {
        status: "locked",
        offer: offerIsPending
          ? null
          : buildPlanUpgradeOffer({
              ...input,
              plans: plans ?? [],
              // Só com o preço pago verificado pelo servidor o plano é nomeado.
              currentPriceVerified: access.plan_price_verified === true,
            }),
      };
    }
    return result;
  }, [features, isAccessPending, accessError, access, isCatalogPending, plans]);
}

/**
 * Um recurso só. `null` (página sem trava de plano) é sempre `allowed` e não
 * pede nada ao catálogo.
 */
export function usePlanFeatureAccess(
  feature: PlanFeatureKey | null
): PlanFeatureAccess {
  const features = useMemo(
    () => (feature ? [feature] : NO_FEATURES),
    [feature]
  );
  const access = usePlanAccess(features);
  if (!feature) return ALLOWED;
  return access[feature] ?? PENDING;
}
